import { useSuspenseQuery } from "@tanstack/react-query";
import { addMonths, differenceInMonths } from "date-fns";
import { useEffect, useState } from "react";
import { twJoin } from "tailwind-merge";

import { NoData } from "@/components/NoData/NoData";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";

const RANGE = "lastYear";

export function TicketClosingTimeServiceLevelsCard(): React.ReactNode {
  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const [minDate] = useState(() => addMonths(new Date(), -12));
  const [maxDate] = useState(() => addMonths(new Date(), -1));
  const [date, setDate] = useState(maxDate);
  const [monthIndex, setMonthIndex] = useState(0);
  const { data } = useSuspenseQuery(analyticsQueries.bucketTicketLeadTimeMonthly(RANGE));

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setMonthIndex(differenceInMonths(maxDate, date));
  }, [date, maxDate]);

  useEffect(() => {
    if (data && monthIndex >= data.length) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setMonthIndex(0);
    }
  }, [data, monthIndex]);

  const currentMonth = data?.[monthIndex];

  const noData =
    !currentMonth ||
    !data ||
    data.every((x) => x.bucket1Percentage === 0 && x.bucket2Percentage === 0 && x.bucket3Percentage === 0);

  return (
    <AnalyticsCard.Root>
      <AnalyticsCard.Header
        title={t("page.analytics-overview.chart.lead-time-benchmark")}
        tooltip={t("page.analytics-overview.chart.lead-time-benchmark.tooltip")}
      >
        <AnalyticsCard.FilterPeriod type="month" value={date} onChange={setDate} min={minDate} max={maxDate} />
      </AnalyticsCard.Header>
      {noData ? (
        <NoData />
      ) : (
        <Inline gap="sm" collapseBelow="sm">
          <Bucket
            label={t("common.date-range.days-or-less", { maxDays: currentMonth.bucket1MaxDays })}
            value={Math.floor(currentMonth.bucket1Percentage)}
            footer={t("page.analytics-overview.chart.lead-time-benchmark.tier-top.description", {
              percentage: currentMonth.bucket1TierPercentage,
            })}
            tooltip={t("page.analytics-overview.chart.lead-time-benchmark.tier-top.tooltip", {
              maxDays: currentMonth.bucket1MaxDays,
            })}
            type="good"
          />
          <Bucket
            label={t("common.date-range.days", {
              minDays: currentMonth.bucket1MaxDays,
              maxDays: currentMonth.bucket2MaxDays,
            })}
            value={Math.floor(currentMonth.bucket2Percentage)}
            footer={t("page.analytics-overview.chart.lead-time-benchmark.tier-mid.description", {
              percentage: currentMonth.bucket2TierPercentage,
            })}
            tooltip={t("page.analytics-overview.chart.lead-time-benchmark.tier-mid.tooltip", {
              minDays: currentMonth.bucket1MaxDays,
              maxDays: currentMonth.bucket2MaxDays,
            })}
            type="average"
          />
          <Bucket
            label={t("common.date-range.days-or-more", { minDays: currentMonth.bucket2MaxDays })}
            value={Math.floor(currentMonth.bucket3Percentage)}
            footer={t("page.analytics-overview.chart.lead-time-benchmark.tier-bottom.description", {
              percentage: currentMonth.bucket3TierPercentage,
            })}
            tooltip={t("page.analytics-overview.chart.lead-time-benchmark.tier-bottom.tooltip", {
              minDays: currentMonth.bucket2MaxDays,
            })}
            type="bad"
          />
        </Inline>
      )}
    </AnalyticsCard.Root>
  );
}

interface BucketProps {
  label: string;
  value: number;
  footer: string;
  tooltip: string;
  type: "good" | "average" | "bad";
}

function Bucket({ label, value, tooltip, footer, type }: BucketProps) {
  return (
    <article className="flex flex-1 flex-col gap-space-sm rounded-md border border-border-default p-space-md">
      <Spread>
        <Inline gap="xs" align="center" wrap={false}>
          <span
            className={twJoin(
              "size-space-sm shrink-0 rounded-full",
              type === "good" && "bg-background-success",
              type === "average" && "bg-background-warning",
              type === "bad" && "bg-background-danger",
            )}
            aria-hidden
          />
          <span className="text-caption text-text-secondary">{label}</span>
        </Inline>
        {tooltip && (
          <div className="flex h-icon-control items-center">
            <Info tooltip={tooltip} variant="secondary" />
          </div>
        )}
      </Spread>
      <span className="text-figure-md text-text-brand-secondary">{Math.floor(value)}%</span>
      <span className="text-overline text-text-secondary">{footer}</span>
    </article>
  );
}
